/**
 * Admin Networks Management Module
 *
 * Bridge CRUD for the Manage Networks page.
 */

import { api } from '../api/client.js';
import type { BridgeDetail, BridgeSambaConfig, CreateBridgeRequest } from '../api/types.js';
import { apiCall } from '../api/helpers.js';
import { escapeHtml, escapeAttr } from '../utils.js';
import { declareLoading, declareLoaded, confirmDialog } from '../ui/dialogs.js';
import { generateToggle, generateToggleList, getToggleListSelected, generateComboSelect, generateNetworkPrefixSelect } from '../ui/forms.js';

let bridgesTable: unknown = null;
let selectedHost: string = '';  // '' = local

/**
 * Get the currently selected network host ('' = local)
 */
export function getSelectedNetHost(): string {
  return selectedHost;
}

/**
 * Set the selected host and reload the bridges table
 */
export async function setNetHost(host: string): Promise<void> {
  selectedHost = host;
  declareLoading();
  await fillBridgesTable();
  declareLoaded();
}

/**
 * Fill the bridges table with detailed bridge information
 */
export async function fillBridgesTable(): Promise<void> {
  const host = selectedHost || undefined;
  const bridges = await apiCall(api.getNetBridges(host), 'Failed to Load Bridges');
  if (!bridges) return;

  // Fetch samba config for each bridge to show AD DC status
  const sambaConfigs = await Promise.all(
    bridges.map(br => apiCall(api.getBridgeSamba(br.name, host), '').catch(() => null))
  );

  const tableData = bridges.map((bridge, idx) => {
    const safeName = escapeAttr(bridge.name);
    const sambaConfig = sambaConfigs[idx];

    // CIDR or DHCP
    let cidrText = '-';
    if (bridge.dhcp_client) {
      cidrText = 'DHCP';
    } else if (bridge.cidr) {
      cidrText = escapeHtml(bridge.cidr);
    }

    // Domain: show AD domain if samba is enabled, otherwise bridge domain
    let domainText = '-';
    if (sambaConfig?.enabled && sambaConfig?.domain) {
      domainText = `<span class="badge badge-info">AD</span> ${escapeHtml(sambaConfig.domain)}`;
    } else if (bridge.domain) {
      domainText = escapeHtml(bridge.domain);
    }

    const vlanText = bridge.vlan_id ? String(bridge.vlan_id) : '-';

    // Flags
    const flags: string[] = [];
    if (bridge.external) flags.push('<span class="badge badge-info">external</span>');
    if (bridge.default) flags.push('<span class="badge badge-success">default</span>');
    if (bridge.externally_managed) flags.push('<span class="badge badge-warn">managed</span>');
    const flagsText = flags.length > 0 ? flags.join(' ') : '-';

    // Actions — hide modify/delete for the external bridge
    let actions = '';
    if (!bridge.external) {
      actions = `
        <button class="ui-button" onclick='modifyBridge("${safeName}")'>Modify</button>
        <button class="ui-button" onclick='deleteBridge("${safeName}")'>Delete</button>
      `;
      // Add export button for bridges with a VLAN
      if (bridge.vlan_id) {
        actions += `<button class="ui-button" onclick='exportBridge("${safeName}")'>Export</button>`;
      }
    }

    return {
      name: escapeHtml(bridge.name),
      cidr: cidrText,
      domain: domainText,
      vlan: vlanText,
      flags: flagsText,
      actions: actions,
    };
  });

  if (!bridgesTable) {
    bridgesTable = $('#bridges').DataTable({
      data: tableData,
      columns: [
        { data: 'name', title: 'Name' },
        { data: 'cidr', title: 'CIDR' },
        { data: 'domain', title: 'Domain' },
        { data: 'vlan', title: 'VLAN' },
        { data: 'flags', title: 'Flags' },
        { data: 'actions', title: 'Actions' },
      ],
      paging: false,
      info: false,
      searching: false,
    });
  } else {
    const dt = bridgesTable as { clear: () => unknown; rows: { add: (data: unknown) => unknown }; draw: () => void };
    dt.clear();
    dt.rows.add(tableData);
    dt.draw();
  }

  declareLoaded();
}

/**
 * Build the bridge form HTML.
 */
async function buildBridgeForm(bridge?: BridgeDetail, sambaConfig?: BridgeSambaConfig | null): Promise<string> {
  const host = selectedHost || undefined;
  // Fetch interfaces and bridge list in parallel
  const [interfaces, bridges] = await Promise.all([
    apiCall(api.getNetInterfaces(host), 'Failed to load interfaces').then(i => i || []),
    apiCall(api.getNetBridges(host), 'Failed to load bridges').then(b => b || []),
  ]);

  const name = bridge?.name || '';
  const cidr = bridge?.cidr || '';
  const domain = bridge?.domain || '';
  const vlanId = bridge?.vlan_id || '';
  const dhcpClient = bridge?.dhcp_client || false;
  const dhcpServer = bridge?.dhcp_server ?? true;
  const externallyManaged = bridge?.externally_managed || false;
  const blockLan = bridge?.block_lan || false;
  const hairpinNat = bridge?.hairpin_nat || false;
  const natUplink = bridge?.nat_uplink || '';
  const gateway = bridge?.gateway || '';
  const hostGateway = bridge?.host_gateway || '';
  const dhcpStart = bridge?.dhcp_range_start || '';
  const dhcpEnd = bridge?.dhcp_range_end || '';
  const selectedUplinks = bridge?.uplinks || [];
  const selectedRoutedBridges = bridge?.routed_bridges || [];
  const selectedNatBridges = bridge?.nat_bridges || [];

  // Samba config values
  const sambaEnabled = sambaConfig?.enabled || false;
  const sambaManaged = sambaConfig?.managed ?? true;
  const sambaDomain = sambaConfig?.domain || '';
  const sambaWorkgroup = sambaConfig?.workgroup || '';
  const sambaAdminUser = sambaConfig?.admin_user || 'Administrator';

  // Build uplinks checkboxes
  let uplinksHtml = '';
  if (interfaces.length > 0) {
    uplinksHtml = generateToggleList('br-uplink', interfaces, selectedUplinks);
  } else {
    uplinksHtml = '<span style="color:#888">No interfaces available</span>';
  }

  // Build NAT uplink dropdown from bridge names
  const natOptions: Record<string, string> = { '': 'None' };
  for (const br of bridges) {
    natOptions[br.name] = br.name;
  }
  const natSelectHtml = generateComboSelect('br-nat-uplink', natUplink, natOptions);

  // Build peer bridge options (all bridges except current one)
  const peerBridgeNames = bridges
    .map(br => br.name)
    .filter(n => n !== name);

  let routedBridgesHtml = '';
  let natBridgesHtml = '';
  if (peerBridgeNames.length > 0) {
    routedBridgesHtml = generateToggleList('br-routed-bridges', peerBridgeNames, selectedRoutedBridges);
    natBridgesHtml = generateToggleList('br-nat-bridges', peerBridgeNames, selectedNatBridges);
  } else {
    const noOther = '<span style="color:#888">No other bridges configured</span>';
    routedBridgesHtml = noOther;
    natBridgesHtml = noOther;
  }

  // Default: auto-allocate for new bridges, manual for existing
  const defaultMode = bridge ? 'manual' : 'auto';

  return `
    <div class="form-field"><label>Name</label><input id="br-name" value="${escapeAttr(name)}" ${bridge ? 'readonly' : ''}></div>
    <div class="form-field"><label>CIDR Mode</label>
      <select id="br-cidr-mode">
        <option value="auto" ${defaultMode === 'auto' ? 'selected' : ''}>Auto-allocate</option>
        <option value="manual" ${defaultMode === 'manual' ? 'selected' : ''}>Manual</option>
      </select>
    </div>
    <div id="br-cidr-auto-section" style="display: ${defaultMode === 'auto' ? 'block' : 'none'}">
      <div class="form-field"><label>Prefix Size</label>${generateNetworkPrefixSelect('br-network-prefix')}
        <div style="font-size: 0.85em; color: var(--color-text-muted); margin-top: 4px;">
          A random RFC1918 CIDR will be allocated.
        </div>
      </div>
    </div>
    <div id="br-cidr-manual-section" style="display: ${defaultMode === 'manual' ? 'block' : 'none'}">
      <div class="form-field"><label>CIDR</label><input id="br-cidr" value="${escapeAttr(cidr)}" placeholder="e.g. 192.168.23.1/24"></div>
    </div>
    <div class="form-field"><label>Gateway</label><input id="br-gateway" value="${escapeAttr(gateway)}" placeholder="DHCP default route for VMs (defaults to bridge IP)"></div>
    <div class="form-field"><label>Host Gateway</label><input id="br-host-gateway" value="${escapeAttr(hostGateway)}" placeholder="Optional route on host via this bridge"></div>
    <div class="form-field"><label>Domain</label><input id="br-domain" value="${escapeAttr(domain)}" placeholder="e.g. vm.local"></div>
    <div class="form-field"><label>VLAN ID</label><input id="br-vlan" type="number" value="${escapeAttr(String(vlanId))}" placeholder="0 = none"></div>
    <div class="form-field"><label>DHCP Client</label><div class="form-value">${generateToggle('br-dhcp-client', dhcpClient)}</div></div>
    <div class="form-field"><label>DHCP Server</label><div class="form-value">${generateToggle('br-dhcp-server', dhcpServer)}</div></div>
    <div class="form-field"><label>Externally Managed</label><div class="form-value">${generateToggle('br-ext-managed', externallyManaged)}</div></div>
    <div class="form-field"><label>NAT Uplink</label>${natSelectHtml}</div>
    <div class="form-field"><label>Block LAN on NAT</label><div class="form-value">${generateToggle('br-block-lan', blockLan)}</div></div>
    <div class="form-field"><label>Hairpin NAT</label><div class="form-value">${generateToggle('br-hairpin-nat', hairpinNat)}</div></div>
    <h4>Uplinks</h4>
    ${uplinksHtml}
    <h4>Routed Bridges</h4>
    ${routedBridgesHtml}
    <h4>NAT Bridges</h4>
    ${natBridgesHtml}
    <h4>DHCP Range</h4>
    <div class="form-field"><label>Start</label><input id="br-dhcp-start" value="${escapeAttr(dhcpStart)}" placeholder="e.g. 192.168.23.100"></div>
    <div class="form-field"><label>End</label><input id="br-dhcp-end" value="${escapeAttr(dhcpEnd)}" placeholder="e.g. 192.168.23.200"></div>

    <h4>Samba AD Domain Controller</h4>
    <div class="form-field"><label>Enable AD DC</label><div class="form-value">${generateToggle('samba-enabled', sambaEnabled)}</div></div>
    <div id="samba-config-section" style="display: ${sambaEnabled ? 'block' : 'none'}">
      <div class="form-field"><label>AD Domain</label><input id="samba-domain" value="${escapeAttr(sambaDomain)}" placeholder="e.g. EXAMPLE.COM"></div>
      <div class="form-field"><label>NetBIOS Name</label><input id="samba-workgroup" value="${escapeAttr(sambaWorkgroup)}" placeholder="Optional (derived from domain)"></div>
      <div class="form-field"><label>Admin User</label><input id="samba-admin-user" value="${escapeAttr(sambaAdminUser)}"></div>
      <div class="form-field"><label>Admin Password</label><input id="samba-admin-password" type="password" placeholder="Required for new DC"></div>
      <div class="form-field"><label>Managed DC</label><div class="form-value">${generateToggle('samba-managed', sambaManaged)}</div></div>
    </div>
  `;
}

/**
 * Setup samba toggle visibility
 */
function setupSambaToggle(): void {
  $('#samba-enabled').on('change', function() {
    const enabled = $(this).is(':checked');
    if (enabled) {
      $('#samba-config-section').slideDown();
    } else {
      $('#samba-config-section').slideUp();
    }
  });
}

/**
 * Setup CIDR mode toggle (auto-allocate vs manual) visibility
 */
function setupCidrModeToggle(): void {
  $('#br-cidr-mode').on('change', function() {
    const mode = $(this).val() as string;
    if (mode === 'auto') {
      $('#br-cidr-auto-section').slideDown();
      $('#br-cidr-manual-section').slideUp();
    } else {
      $('#br-cidr-auto-section').slideUp();
      $('#br-cidr-manual-section').slideDown();
    }
  });
}

/**
 * Collect bridge form values into an object.
 */
function collectBridgeForm(): CreateBridgeRequest {
  const result: Record<string, unknown> = {};

  result.name = ($('#br-name').val() as string || '').trim();

  const cidrMode = ($('#br-cidr-mode').val() as string || 'manual');
  if (cidrMode === 'auto') {
    const prefix = ($('#br-network-prefix').val() as string || '').trim();
    if (prefix) result.network_prefix = parseInt(prefix, 10);
  } else {
    const cidr = ($('#br-cidr').val() as string || '').trim();
    if (cidr) result.cidr = cidr; else result.cidr = null;
  }
  const gw = ($('#br-gateway').val() as string || '').trim();
  if (gw) result.gateway = gw; else result.gateway = null;
  const hgw = ($('#br-host-gateway').val() as string || '').trim();
  if (hgw) result.host_gateway = hgw; else result.host_gateway = null;
  const domain = ($('#br-domain').val() as string || '').trim();
  if (domain) result.domain = domain; else result.domain = null;
  const vlan = parseInt($('#br-vlan').val() as string || '0', 10);
  result.vlan_id = isNaN(vlan) ? 0 : vlan;
  result.dhcp_client = $('#br-dhcp-client').is(':checked');
  result.dhcp_server = $('#br-dhcp-server').is(':checked');
  result.externally_managed = $('#br-ext-managed').is(':checked');
  result.block_lan = $('#br-block-lan').is(':checked');
  result.hairpin_nat = $('#br-hairpin-nat').is(':checked');
  const nat = ($('#br-nat-uplink').val() as string || '').trim();
  if (nat) result.nat_uplink = nat; else result.nat_uplink = null;
  result.uplinks = getToggleListSelected('br-uplink');
  result.routed_bridges = getToggleListSelected('br-routed-bridges');
  result.nat_bridges = getToggleListSelected('br-nat-bridges');
  const dhcpStart = ($('#br-dhcp-start').val() as string || '').trim();
  const dhcpEnd = ($('#br-dhcp-end').val() as string || '').trim();
  if (dhcpStart && dhcpEnd) {
    result.dhcp_range_start = dhcpStart;
    result.dhcp_range_end = dhcpEnd;
  } else {
    result.dhcp_range_start = null;
    result.dhcp_range_end = null;
  }

  return result as unknown as CreateBridgeRequest;
}

/**
 * Validate that routed_bridges and nat_bridges don't overlap.
 * Returns an error message string or null if valid.
 */
function validateBridgeForm(data: Partial<BridgeDetail>): string | null {
  const routed = data.routed_bridges || [];
  const nat = data.nat_bridges || [];
  const overlap = routed.filter(b => nat.includes(b));
  if (overlap.length > 0) {
    return `Bridge "${overlap[0]}" cannot appear in both Routed Bridges and NAT Bridges`;
  }
  return null;
}

/**
 * Collect samba config from form.
 */
function collectSambaForm(): Partial<BridgeSambaConfig> {
  const enabled = $('#samba-enabled').is(':checked');
  if (!enabled) {
    return { enabled: false };
  }

  const result: Partial<BridgeSambaConfig> = {
    enabled: true,
    managed: $('#samba-managed').is(':checked'),
    domain: ($('#samba-domain').val() as string || '').trim(),
    workgroup: ($('#samba-workgroup').val() as string || '').trim() || undefined,
    admin_user: ($('#samba-admin-user').val() as string || '').trim(),
  };

  const password = ($('#samba-admin-password').val() as string || '').trim();
  if (password) {
    result.admin_password = password;
  }

  return result;
}

/**
 * Create a new bridge
 */
export async function createBridge(): Promise<void> {
  declareLoading();
  const form = await buildBridgeForm();
  declareLoaded();

  const host = selectedHost || undefined;
  let saving = false;  // Guard against double-submission

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Create Bridge</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Create Bridge',
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      open: function() {
        setupCidrModeToggle();
        setupSambaToggle();
      },
      buttons: {
        Create: async function () {
          if (saving) return;  // Prevent double-click
          saving = true;

          const data = collectBridgeForm();
          if (!data.name) {
            alert('Please enter a bridge name');
            saving = false;
            return;
          }
          const bridgeErr = validateBridgeForm(data);
          if (bridgeErr) {
            alert(bridgeErr);
            saving = false;
            return;
          }
          const sambaData = collectSambaForm();
          if (sambaData.enabled && !sambaData.domain) {
            alert('AD Domain is required when Samba AD DC is enabled');
            saving = false;
            return;
          }
          const parent = $(this);
          declareLoading();

          // Create bridge first
          const result = await apiCall(api.createBridge(data, host), 'Failed to Create Bridge');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          // Then save samba config if enabled
          if (sambaData.enabled) {
            await apiCall(api.updateBridgeSamba(data.name, sambaData, host), 'Failed to Save Samba Config');
          }

          declareLoaded();
          parent.dialog('close');
          fillBridgesTable();
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Modify an existing bridge
 */
export async function modifyBridge(name: string): Promise<void> {
  declareLoading();

  const host = selectedHost || undefined;
  // Fetch bridge and samba config in parallel
  const [bridge, sambaConfig] = await Promise.all([
    apiCall(api.getBridge(name, host), 'Failed to Load Bridge'),
    apiCall(api.getBridgeSamba(name, host), '').catch(() => null),
  ]);

  if (!bridge) { declareLoaded(); return; }

  const form = await buildBridgeForm(bridge, sambaConfig);
  declareLoaded();

  let saving = false;  // Guard against double-submission

  $('<div></div>')
    .appendTo('body')
    .html('<div><h4>Modify Bridge</h4>' + form + '</div>')
    .dialog({
      modal: true,
      title: 'Modify Bridge: ' + escapeHtml(name),
      zIndex: 10000,
      autoOpen: true,
      width: 500,
      resizable: false,
      open: function() {
        setupCidrModeToggle();
        setupSambaToggle();
      },
      buttons: {
        Save: async function () {
          if (saving) return;  // Prevent double-click
          saving = true;

          const data = collectBridgeForm();
          const bridgeErr = validateBridgeForm(data);
          if (bridgeErr) {
            alert(bridgeErr);
            saving = false;
            return;
          }
          const sambaData = collectSambaForm();
          if (sambaData.enabled && !sambaData.domain) {
            alert('AD Domain is required when Samba AD DC is enabled');
            saving = false;
            return;
          }
          const parent = $(this);
          declareLoading();

          // Update bridge
          const result = await apiCall(api.updateBridge(name, data, host), 'Failed to Update Bridge');
          if (!result) {
            declareLoaded();
            saving = false;
            return;
          }

          // Only update samba config if it was enabled before or is enabled now
          const wasEnabled = sambaConfig?.enabled || false;
          const isEnabled = sambaData.enabled || false;
          if (wasEnabled || isEnabled) {
            await apiCall(api.updateBridgeSamba(name, sambaData, host), 'Failed to Save Samba Config');
          }

          declareLoaded();
          parent.dialog('close');
          fillBridgesTable();
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

/**
 * Delete a bridge
 */
export async function deleteBridge(name: string): Promise<void> {
  const host = selectedHost || undefined;
  confirmDialog(
    `Are you sure you want to delete bridge '${escapeHtml(name)}'?`,
    async () => {
      declareLoading();
      // Remove samba config first
      await apiCall(api.updateBridgeSamba(name, { enabled: false }, host), '').catch(() => {});
      await apiCall(api.deleteBridge(name, host), 'Failed to Delete Bridge');
      await fillBridgesTable();
      declareLoaded();
    }
  );
}

/**
 * Apply network configuration (create/update bridges)
 */
export async function applyNetworks(): Promise<void> {
  const host = selectedHost || undefined;
  declareLoading();
  const result = await apiCall(api.applyNetworks(host), 'Failed to Apply Networks');
  declareLoaded();
  if (result) {
    const { notificationDialog } = await import('../ui/dialogs.js');
    notificationDialog(
      '<p>Network configuration applied successfully.</p>' +
      '<p style="margin-top:1em;color:#888;font-size:0.9em;">' +
      '<strong>Note:</strong> Bridge deletions and VLAN changes require a system restart or manual intervention to take effect.</p>',
      true  // allowHtml
    );
  }
}

/**
 * Export a bridge as a delegated network to one or more remote hosts.
 * Creates a stripped-down copy (name + VLAN + uplinks only, no CIDR/domain/DHCP/NAT).
 */
export async function exportBridge(name: string): Promise<void> {
  const host = selectedHost || undefined;

  declareLoading();
  const [bridge, hostsMap] = await Promise.all([
    apiCall(api.getBridge(name, host), 'Failed to Load Bridge'),
    apiCall(api.getHostsForDropdown(), 'Failed to Load Hosts'),
  ]);
  declareLoaded();

  if (!bridge || !hostsMap) return;

  // Build list of remote hosts (exclude local entry with empty-string key)
  const remoteHosts = Object.entries(hostsMap).filter(([key]) => key !== '');
  if (remoteHosts.length === 0) {
    const { notificationDialog } = await import('../ui/dialogs.js');
    notificationDialog('No remote hosts available for export.');
    return;
  }

  // Build checkboxes for target host selection
  const hostNames = remoteHosts.map(([key]) => key);
  const checkboxes = generateToggleList('export-target', hostNames, []);

  $('<div></div>')
    .appendTo('body')
    .html(
      `<div>
        <p>Export bridge <strong>${escapeHtml(name)}</strong> as a delegated network to remote host(s).</p>
        <p style="font-size:0.9em;color:#888;">Only the bridge name, VLAN ID, and uplinks will be exported. CIDR, domain, DHCP, and NAT settings are omitted.</p>
        <h4>Target Hosts</h4>
        ${checkboxes}
      </div>`
    )
    .dialog({
      modal: true,
      title: 'Export Delegated Network',
      zIndex: 10000,
      autoOpen: true,
      width: 450,
      resizable: false,
      buttons: {
        Export: async function () {
          const targets = getToggleListSelected('export-target');
          if (targets.length === 0) {
            alert('Please select at least one target host');
            return;
          }

          const parent = $(this);
          declareLoading();

          const exportBody: Partial<CreateBridgeRequest> = {
            name: bridge.name,
            vlan_id: bridge.vlan_id || 0,
            uplinks: bridge.uplinks || [],
            dhcp_server: false,
          };

          const results: string[] = [];
          for (const targetHost of targets) {
            const res = await apiCall(
              api.createBridge(exportBody as CreateBridgeRequest, targetHost),
              ''
            );
            if (res) {
              results.push(`${escapeHtml(targetHost)}: OK`);
            } else {
              results.push(`${escapeHtml(targetHost)}: Failed`);
            }
          }

          declareLoaded();
          parent.dialog('close');

          const { notificationDialog } = await import('../ui/dialogs.js');
          notificationDialog(
            '<p>Export results:</p><ul>' +
            results.map(r => `<li>${r}</li>`).join('') +
            '</ul>',
            true
          );
        },
        Cancel: function () {
          $(this).dialog('close');
        },
      },
      close: function () {
        $(this).remove();
      },
    });
}

// Export to window for onclick handlers
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).fillBridgesTable = fillBridgesTable;
  (window as unknown as Record<string, unknown>).createBridge = createBridge;
  (window as unknown as Record<string, unknown>).modifyBridge = modifyBridge;
  (window as unknown as Record<string, unknown>).deleteBridge = deleteBridge;
  (window as unknown as Record<string, unknown>).applyNetworks = applyNetworks;
  (window as unknown as Record<string, unknown>).exportBridge = exportBridge;
  (window as unknown as Record<string, unknown>).setNetHost = setNetHost;
}
